import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import Headline from '../../Elements/Headline'
import Text from '../../Elements/Text'
import Button from '../../Formik/Button'
import Input from '../../Formik/Input'
import { useInviteUser, useSheet, useUpdateSheet } from '../lib/Hooks/Sheet'
import { getImperativName, getSingularName } from './lib/Naming'

const Share = () => {
  const { t } = useTranslation(['configuration', 'common'])
  const { data: sheet } = useSheet()
  const { mutate: updateSheet, isPending: isSaving } = useUpdateSheet()
  const { mutate: inviteUserToSheet, isPending: isInviting } = useInviteUser()

  const uninviteUser = async () => {
    if (
      !window.confirm(
        t('configuration:share.confirmUninvite', { email: sheet!.invites }),
      )
    ) {
      return
    }

    const newSheet = structuredClone(sheet)
    newSheet!.invites = undefined

    updateSheet(newSheet!)
  }

  const inviteUser = async (email: string) => {
    if (
      !window.confirm(
        t('configuration:share.confirmInvite', {
          name: getImperativName(sheet!.sheet_type),
          email,
        }),
      )
    ) {
      return
    }

    const newSheet = structuredClone(sheet)
    newSheet!.invites = email

    inviteUserToSheet(newSheet!)
  }

  const removeUser = async () => {
    if (
      !window.confirm(
        t('configuration:share.confirmRemove', {
          name: sheet!.user?.name || sheet!.user?.email,
        }),
      )
    ) {
      return
    }

    const newSheet = structuredClone(sheet)
    newSheet!.user = undefined

    updateSheet(newSheet!)
  }

  return (
    <>
      <Headline className='mb-5'>
        {t('configuration:share.heading', {
          name: getImperativName(sheet!.sheet_type),
        })}
      </Headline>
      {sheet!.user && (
        <>
          <Text className='mb-4'>
            {t('configuration:share.sharedWithPrefix', {
              name: getImperativName(sheet!.sheet_type),
            })}{' '}
            <strong>{sheet!.user.name || sheet!.user.email}</strong>
            {t('configuration:share.sharedWithSuffix')}
          </Text>
          <Button
            theme='red'
            className='w-full'
            onClick={removeUser}
            loading={isSaving}
          >
            {t('configuration:share.removeButton')}
          </Button>
        </>
      )}
      {!sheet!.user && (
        <>
          {sheet!.invites && (
            <Text className='mt-4'>
              {t('configuration:share.invitedPrefix')}{' '}
              <strong>{sheet!.invites}</strong>{' '}
              {t('configuration:share.invitedSuffix', {
                name: getImperativName(sheet!.sheet_type),
              })}
              <br />
              {t('configuration:share.cancelPrefix')}{' '}
              <u className='cursor-pointer' onClick={uninviteUser}>
                {t('configuration:share.cancelLinkText')}
              </u>{' '}
              {t('configuration:share.cancelSuffix')}
            </Text>
          )}
          {!sheet!.invites && (
            <Formik
              initialValues={{
                email: '',
              }}
              validationSchema={Yup.object({
                email: Yup.string()
                  .email(t('configuration:share.invalidEmail'))
                  .required(t('common:validation.required')),
              })}
              onSubmit={(values) => inviteUser(values.email)}
            >
              <Form>
                <Text className='mb-2'>
                  {t('configuration:share.description', {
                    name: getImperativName(sheet!.sheet_type),
                  })}
                </Text>
                <Text className='mb-2'>
                  {t('configuration:share.tip', {
                    name: getSingularName(sheet!.sheet_type),
                  })}
                </Text>
                <Text>
                  {t('configuration:share.instructions', {
                    name: getImperativName(sheet!.sheet_type),
                  })}
                </Text>
                <div className='flex mt-4'>
                  <Input
                    placeholder={t('configuration:share.emailPlaceholder')}
                    type='email'
                    name='email'
                    className='flex-1'
                  />
                  <Button type='submit' loading={isInviting} className='ml-2'>
                    {t('configuration:share.inviteButton')}
                  </Button>
                </div>
              </Form>
            </Formik>
          )}
        </>
      )}
    </>
  )
}

export default Share
